import React, { useState, useEffect } from 'react';
import ESP32DownloadCode from './ESP32DownloadCode';
import { log, error } from '../utils/logger'; // Importar el nuevo componente

const DevicesContent = ({ devices, setDevices, userId, isDarkMode, currentPlanLimits }) => {
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceSerial, setNewDeviceSerial] = useState('');
  const [newDeviceType, setNewDeviceType] = useState('');
  // newDeviceControls ahora es un array de objetos { name, description, unit, pin_number, selected }
  const [newDeviceControls, setNewDeviceControls] = useState([]); 
  const [homologatedDevices, setHomologatedDevices] = useState([]);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [deviceToDownload, setDeviceToDownload] = useState(null);
  const [isAddingCustomDevice, setIsAddingCustomDevice] = useState(false);
  const [serialError, setSerialError] = useState('');



  const API_BASE_URL = 'https://holmio.net/app/api.php';

  const bgColorClass = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textColorClass = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const inputBgClass = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-900 border-gray-300';
  const buttonClass = isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white';
  const cancelButtonClass = isDarkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-400 hover:bg-gray-500 text-white';
  const tableHeaderClass = isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700';
  const tableRowClass = isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50';

  // Cargar dispositivos homologados al inicio
  useEffect(() => {
    const fetchHomologatedDevices = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/homologated_devices`);
        if (!response.ok) {
          const errorText = await response.text();
          error('Error HTTP al cargar dispositivos homologados:', response.status, response.statusText, errorText);
          throw new Error(`Error HTTP: ${response.status} ${response.statusText} - ${errorText}`);
        }
        const data = await response.json();
        if (data.status === 'success') {
          // Asegurarse de que los controles se parseen si vienen como string JSON
          const parsedHomologated = data.data.map(dev => ({
            ...dev,
            controls: typeof dev.controls === 'string' ? JSON.parse(dev.controls) : dev.controls
          }));
          setHomologatedDevices(parsedHomologated);
          log("Dispositivos homologados cargados:", parsedHomologated); // Para depuración
        } else {
          error('Error al cargar dispositivos homologados:', data.message);
        }
      } catch (error) {
        error('Error de red o servidor al cargar dispositivos homologados:', error);
      }
    };
    fetchHomologatedDevices();
  }, [API_BASE_URL]);

  const validateSerial = async (serial) => {
    if (!serial) {
      setSerialError('');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/check_serial?serial=${serial}`);
      const data = await res.json();
      if (data.exists) {
        setSerialError('Este número de serie ya está registrado.');
      } else {
        setSerialError('');
      }
    } catch (err) {
      console.error('Error validando serial:', err);
      setSerialError('Error al validar el serial.');
    }
  };


  // Manejar cambio de tipo de dispositivo homologado
  const handleDeviceTypeChange = (e) => {
    const selectedType = e.target.value;
    setNewDeviceType(selectedType);
    
    const selectedHomologated = homologatedDevices.find(dev => dev.type === selectedType);
    if (selectedHomologated && Array.isArray(selectedHomologated.controls)) {
      // Mapear los controles homologados para incluir descripción, unidad, pin_number y un estado 'selected'
      const mappedControls = selectedHomologated.controls.map(controlName => ({
        name: controlName, // Aquí 'controlName' es directamente el nombre del control (string)
        description: '', 
        unit: '',
        pin_number: '', // <-- CAMBIO: Inicializar el campo pin_number
        selected: false, // Por defecto, no seleccionado al agregar
      }));
      setNewDeviceControls(mappedControls);
      log("Controles disponibles para", selectedType, ":", mappedControls); // Para depuración
    } else {
      setNewDeviceControls([]);
      log("No se encontraron controles para el tipo:", selectedType); // Para depuración
    }
  };

  // Manejar la selección/deselección de controles
  const handleControlCheckboxChange = (controlName) => {
    setNewDeviceControls(prevControls =>
      prevControls.map(control =>
        control.name === controlName ? { ...control, selected: !control.selected } : control
      )
    );
  };

  // Manejar cambio en cualquier campo de un control (descripción, unidad, pin_number)
  const handleControlFieldChange = (controlName, field, value) => {
    setNewDeviceControls(prevControls =>
      prevControls.map(c =>
        c.name === controlName ? { ...c, [field]: value } : c
      )
    );
  };


  const handleAddDevice = async (e) => {
    e.preventDefault();
    if (devices.length >= currentPlanLimits.devices) {
      alert(`Has alcanzado el límite de ${currentPlanLimits.devices} dispositivos para tu plan actual.`);
      return;
    }
    if (serialError) {
      alert('Corrige el error en el número de serie antes de continuar.');
      return;
    }


    // Filtrar solo los controles seleccionados antes de enviar
    const controlsToSend = newDeviceControls.filter(control => control.selected).map(control => ({
      name: control.name,
      description: control.description,
      unit: control.unit,
      pin_number: control.pin_number === '' ? null : parseInt(control.pin_number, 10), // <-- CAMBIO: Parsear a int o null
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          name: newDeviceName,
          serial: newDeviceSerial,
          type: newDeviceType,
          controls: controlsToSend, // newDeviceControls ya es un array de objetos
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        error('Error HTTP al agregar dispositivo:', response.status, response.statusText, errorText);
        throw new Error(`Error HTTP: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        // El backend ya debería devolver los controles en el formato correcto (array de objetos)
        const addedDevice = {
          ...data.data,
          controls: Array.isArray(data.data.controls) ? data.data.controls : [],
        };
        setDevices([...devices, addedDevice]);
        setNewDeviceName('');
        setNewDeviceSerial('');
        setNewDeviceType('');
        setNewDeviceControls([]);
        setIsAddingDevice(false);
        log("Dispositivo agregado con éxito:", addedDevice); // Para depuración
      } else {
        alert(`Error al agregar dispositivo: ${data.message}`);
        error("Error de backend al agregar dispositivo:", data.message); // Para depuración
      }
    } catch (error) {
      error('Error de red, HTTP o parseo de JSON al agregar dispositivo:', error);
      alert(`Error al conectar con el servidor para agregar dispositivo. Detalles: ${error.message || error}`);
    }
  };
  const handleAddCustomDevice = async (e) => {
      e.preventDefault();

      try {
        const response = await fetch(`${API_BASE_URL}/devices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            name: newDeviceName,
            serial: newDeviceSerial,
            type: 'custom', // Marcamos este tipo
            controls: [], // Inicialmente vacío, se podrá editar después
          }),
        });

        const data = await response.json();
        if (data.status === 'success') {
          setDevices([...devices, data.data]);
          setNewDeviceName('');
          setNewDeviceSerial('');
          setIsAddingCustomDevice(false);
        } else {
          alert(`Error: ${data.message}`);
        }
      } catch (err) {
        alert(`Error al agregar dispositivo personalizado: ${err.message}`);
      }
    };

  const handleEditDevice = (device) => {
    setEditingDeviceId(device.id);
    setNewDeviceName(device.name);
    setNewDeviceSerial(device.serial || '');
    setNewDeviceType(device.type);
    
    // Al editar, necesitamos cargar todos los controles homologados para ese tipo
    // y marcar como seleccionados los que ya tiene el dispositivo.
    const selectedHomologated = homologatedDevices.find(dev => dev.type === device.type);
    let controlsToLoad = [];

    if (selectedHomologated && Array.isArray(selectedHomologated.controls)) {
      const existingControlsMap = new Map(device.controls.map(c => [c.name, c]));
      controlsToLoad = selectedHomologated.controls.map(homologatedControlName => { // Aquí es 'homologatedControlName'
        const existingControl = existingControlsMap.get(homologatedControlName);
        return {
          name: homologatedControlName,
          description: existingControl ? existingControl.description : '',
          unit: existingControl ? existingControl.unit : '',
          pin_number: existingControl ? (existingControl.pin_number !== null ? existingControl.pin_number : '') : '', // <-- CAMBIO: Cargar pin_number
          selected: !!existingControl, // Si existe en el dispositivo, está seleccionado
        };
      });
    } else {
      // Si no hay homologados, solo cargar los controles del dispositivo y marcarlos como seleccionados
      controlsToLoad = device.controls.map(control => ({ 
        ...control, 
        pin_number: control.pin_number !== null ? control.pin_number : '', // <-- CAMBIO: Cargar pin_number
        selected: true 
      }));
    }
    setNewDeviceControls(controlsToLoad);
    setIsAddingDevice(true); // Abrir el formulario en modo edición
  };

  const handleUpdateDevice = async (e) => {
    e.preventDefault();
    // Filtrar solo los controles seleccionados antes de enviar
    const controlsToSend = newDeviceControls.filter(control => control.selected).map(control => ({
      name: control.name,
      description: control.description,
      unit: control.unit,
      pin_number: control.pin_number === '' ? null : parseInt(control.pin_number, 10), // <-- CAMBIO: Parsear a int o null
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/devices`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingDeviceId,
          name: newDeviceName,
          serial: newDeviceSerial,
          type: newDeviceType,
          controls: controlsToSend, // newDeviceControls ya es un array de objetos
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        error('Error HTTP al actualizar dispositivo:', response.status, response.statusText, errorText);
        throw new Error(`Error HTTP: ${response.status} ${response.statusText} - ${errorText}`);
      }
      const data = await response.json();
      
      if (data.status === 'success') {
        // El backend ya debería devolver los controles en el formato correcto (array de objetos)
        const updatedDevice = {
          ...data.data,
          controls: Array.isArray(data.data.controls) ? data.data.controls : [],
        };
        setDevices(devices.map(dev => (dev.id === editingDeviceId ? updatedDevice : dev)));
        setNewDeviceName('');
        setNewDeviceSerial('');
        setNewDeviceType('');
        setNewDeviceControls([]);
        setEditingDeviceId(null);
        setIsAddingDevice(false); // Cerrar el formulario
        log("Dispositivo actualizado con éxito:", updatedDevice); // Para depuración
      } else {
        alert(`Error al actualizar dispositivo: ${data.message}`);
        error("Error de backend al actualizar dispositivo:", data.message); // Para depuración
      }
    } catch (error) {
      error('Error de red, HTTP o parseo de JSON al actualizar dispositivo:', error);
      alert(`Error al conectar con el servidor para actualizar dispositivo. Detalles: ${error.message || error}`);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este dispositivo?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/devices`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: deviceId }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          error('Error HTTP al eliminar dispositivo:', response.status, response.statusText, errorText);
          throw new Error(`Error HTTP: ${response.status} ${response.statusText} - ${errorText}`);
        }
        const data = await response.json();
      
        if (data.status === 'success') {
          setDevices(devices.filter(dev => dev.id !== deviceId));
          log("Dispositivo eliminado con éxito:", deviceId); // Para depuración
        } else {
          alert(`Error al eliminar dispositivo: ${data.message}`);
          error("Error de backend al eliminar dispositivo:", data.message); // Para depuración
        }
      } catch (error) {
        error('Error de red, HTTP o parseo de JSON al eliminar dispositivo:', error);
        alert(`Error al conectar con el servidor para eliminar dispositivo. Detalles: ${error.message || error}`);
      }
    }
  };

  const generateEsp32Code = (device) => {
    // controlsArray ahora es un array de objetos { name, description, unit }
    const controlsArray = Array.isArray(device.controls) ? device.controls : [];
    // Para el código del ESP32, solo necesitamos los nombres de los controles
    const controlsString = controlsArray.map(control => `"${control.name}"`).join(', ');
    const code = ``;
    return code;
  };

  const handleDownloadProgram = (device) => {
    setDeviceToDownload(device);
    setShowDownloadModal(true);
  };

  const closeDownloadModal = () => {
    setDeviceToDownload(null);
    setShowDownloadModal(false);
  };

  return (
    <div className={`flex-1 p-6 rounded-lg shadow-xl m-4 ${bgColorClass} ${textColorClass} transition-colors duration-300`}>
      <h2 className="text-3xl font-bold mb-6 text-center">Mis Dispositivos</h2>

      {/* Botón para agregar nuevo dispositivo */}
      {!isAddingDevice && !editingDeviceId && (
        <div className="flex justify-center space-x-4 mb-8">
          <button
            onClick={() => {
              if (devices.length >= currentPlanLimits.devices) {
              alert(`Has alcanzado el límite de ${currentPlanLimits.devices} dispositivos para tu plan actual.`);
              return;
            }
            setIsAddingDevice(true)
          }}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-300 ${buttonClass}`}
          >
            Agregar Dispositivo Homologado
          </button>
        </div>

      )}

      {/* Tabla de Dispositivos */}
      {devices.length > 0 ? (
        <div className="overflow-x-auto rounded-lg shadow-md">
          <table className="min-w-full table-auto divide-y divide-gray-200">
            <thead className={tableHeaderClass}>
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider">Nombre</th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider">Tipo</th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider">Serie</th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider">Controles</th>
                <th scope="col" className="px-4 py-2 text-center text-xs font-medium uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>
              {devices.map((device) => (
                <tr key={device.id} className={tableRowClass}>
                  <td className="px-4 py-2 text-sm">{device.name}</td>
                  <td className="px-4 py-2 text-sm">{device.type}</td>
                  <td className="px-4 py-2 text-sm">{device.serial || 'N/A'}</td>
                  <td className="px-4 py-2 text-sm max-w-xs overflow-hidden text-ellipsis">
                    {/* Mostrar nombres de controles y sus descripciones/unidades */}
                    {Array.isArray(device.controls) && device.controls.length > 0 ?
                      device.controls.map((c, idx) => ( 
                        <span key={c.name || idx}> {/* Usar c.name como key si existe, sino idx */}
                          {c.name}{c.description ? ` (${c.description})` : ''}{c.unit ? ` [${c.unit}]` : ''}{idx < device.controls.length - 1 ? ', ' : ''}
                        </span>
                      ))
                      : 'N/A'}
                  </td>
                  <td className="px-4 py-2 text-center text-sm font-medium">
                    <div className="flex flex-col items-center justify-center space-y-1">
                      <button
                        onClick={() => handleEditDevice(device)}
                        className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'} transition-colors duration-200`}
                        title="Editar"
                      >
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L15.232 5.232z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteDevice(device.id)}
                        className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'} transition-colors duration-200`}
                        title="Eliminar"
                      >
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDownloadProgram(device)}
                        className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'} transition-colors duration-200`}
                        title="Descargar Programa"
                      >
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center text-lg mt-8">No tienes dispositivos agregados. ¡Agrega uno para empezar!</p>
      )}

      {/* Formulario de Agregar/Editar Dispositivo */}
      {(isAddingDevice || editingDeviceId) && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}>
          <div className={`p-6 rounded-lg shadow-xl max-w-md w-full ${bgColorClass} max-h-[90vh] overflow-y-auto`}> {/* Agregado max-h y overflow-y-auto */}
            <h3 className="text-2xl font-bold mb-4 text-center">{editingDeviceId ? 'Editar Dispositivo' : 'Agregar Dispositivo'}</h3>
            <form onSubmit={editingDeviceId ? handleUpdateDevice : handleAddDevice} className="space-y-4">
              <div>
                <label htmlFor="deviceName" className="block text-sm font-medium mb-1">Nombre del Dispositivo</label>
                <input
                  type="text"
                  id="deviceName"
                  value={newDeviceName}
                   onChange={(e) => {
                    setNewDeviceName(e.target.value);
                    validateSerial(e.target.value);
                  }}
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgClass}`}
                  required
                />
                {serialError && <p className="text-red-500 text-sm mt-1">{serialError}</p>}
              </div>
              <div>
                <label htmlFor="deviceSerial" className="block text-sm font-medium mb-1">Serie del Dispositivo</label>
                <input
                  type="text"
                  id="deviceSerial"
                  value={newDeviceSerial}
                  onChange={(e) => setNewDeviceSerial(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgClass}`}
                  required
                />
              </div>
              <div>
                <label htmlFor="deviceType" className="block text-sm font-medium mb-1">Tipo de Dispositivo</label>
                <select
                  id="deviceType"
                  value={newDeviceType}
                  onChange={handleDeviceTypeChange} // Aquí se usa la función
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgClass}`}
                  required
                >
                  <option value="">Selecciona un tipo</option>
                  {homologatedDevices.map(device => (
                    <option key={device.id} value={device.type}>{device.type}</option>
                  ))}
                </select>
              </div>
              {/* Sección de Elementos a Controlar */}
              <div>
                <label className="block text-sm font-medium mb-1">Elementos a Controlar</label>
                {newDeviceControls.length > 0 ? (
                  <div className="space-y-2">
                    {newDeviceControls.map((control, index) => {
                      return (
                        <div key={control.name} className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id={`control-${control.name}`}
                              value={control.name}
                              checked={control.selected}
                              onChange={() => handleControlCheckboxChange(control.name)}
                              className={`h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600' : ''}`}
                            />
                            <label htmlFor={`control-${control.name}`} className={`ml-2 text-sm font-semibold ${textColorClass}`}>{control.name}</label>
                          </div>
                          {control.selected && ( // Solo mostrar inputs si el checkbox está marcado
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                              <div>
                                <label htmlFor={`desc-${control.name}`} className="block text-xs font-medium mb-1">Descripción</label>
                                <input
                                  type="text"
                                  id={`desc-${control.name}`}
                                  value={control.description}
                                  onChange={(e) => handleControlFieldChange(control.name, 'description', e.target.value)}
                                  className={`w-full px-3 py-1 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${inputBgClass}`}
                                  placeholder="Ej. Encendido/Apagado"
                                />
                              </div>
                              {/* MODIFICACIÓN AQUÍ: Contenedor flex para Unidad y Campo Adicional */}
                              <div className="flex items-end gap-2"> {/* Usamos items-end para alinear las etiquetas */}
                                <div className="flex-1"> {/* Ocupa el espacio restante */}
                                  <label htmlFor={`unit-${control.name}`} className="block text-xs font-medium mb-1">Unidad</label>
                                  <input
                                    type="text"
                                    id={`unit-${control.name}`}
                                    value={control.unit}
                                    onChange={(e) => handleControlFieldChange(control.name, 'unit', e.target.value)}
                                    className={`w-full px-3 py-1 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${inputBgClass}`}
                                    placeholder="Ej. °C, %, V"
                                  />
                                </div>
                                {/* CAMBIO: Campo para pin_number */}
                                <div className="flex-1"> {/* Ocupa el espacio restante */}
                                  <label htmlFor={`pin-${control.name}`} className="block text-xs font-medium mb-1">PIN:</label>
                                  <input
                                    type="number" // CAMBIO: type="number"
                                    id={`pin-${control.name}`}
                                    value={control.pin_number || ''} // CAMBIO: usa control.pin_number
                                    onChange={(e) => handleControlFieldChange(control.name, 'pin_number', e.target.value)} // CAMBIO: guarda en pin_number
                                    className={`w-full px-3 py-1 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${inputBgClass}`}
                                    placeholder="Ej. 2, 4, 16"
                                    min="0" // Opcional: rango de pines
                                    max="39" // Opcional: rango de pines
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-red-500">
                    {newDeviceType ? 'No hay controles disponibles para este tipo de dispositivo.' : 'Selecciona un tipo de dispositivo para ver los controles disponibles.'}
                  </p>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingDevice(false);
                    setEditingDeviceId(null);
                    setNewDeviceName('');
                    setNewDeviceSerial('');
                    setNewDeviceType('');
                    setNewDeviceControls([]);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${cancelButtonClass}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${buttonClass}`}
                >
                  {editingDeviceId ? 'Actualizar' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAddingCustomDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`p-6 rounded-lg shadow-xl max-w-md w-full ${bgColorClass} max-h-[90vh] overflow-y-auto`}>
            <h3 className="text-2xl font-bold mb-4 text-center">Agregar Dispositivo Personalizado</h3>
            <form onSubmit={handleAddCustomDevice} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre del Dispositivo</label>
                <input
                  type="text"
                  value={newDeviceName}
                  onChange={(e) => setNewDeviceName(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${inputBgClass}`}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Serie del Dispositivo</label>
                <input
                  type="text"
                  value={newDeviceSerial}
                  onChange={(e) => setNewDeviceSerial(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${inputBgClass}`}
                  required
                />
              </div>
              {/* Aquí podrías añadir inputs dinámicos para agregar controles personalizados */}
              <p className="text-sm text-gray-500">Después podrás asignar controles personalizados.</p>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsAddingCustomDevice(false)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${cancelButtonClass}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${isDarkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                >
                  Agregar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal para Descargar Código ESP32 */}
      {showDownloadModal && deviceToDownload && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}>
          <div className={`p-6 rounded-lg shadow-xl max-w-2xl w-full ${bgColorClass} max-h-[90vh] overflow-y-auto`}>
            <h3 className="text-2xl font-bold mb-4 text-center">Código para {deviceToDownload.name} ({deviceToDownload.serial})</h3>
            {/* CAMBIO AQUÍ: Pasar el objeto deviceToDownload completo como selectedDevice */}
            <ESP32DownloadCode deviceSerial={deviceToDownload.serial} selectedDevice={deviceToDownload} />
            <div className="flex justify-end mt-4">
              <button
                onClick={closeDownloadModal}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${cancelButtonClass}`}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesContent;